import {
	DEFAULT_SKILL_LUCIDE_ICON,
	normalizeSkillLucideIcon,
} from "@repo/utils";
import { SKILL_LUCIDE_ICON_NODE_BY_NAME } from "./skill-lucide-icon-mask-registry";

/** Lucide design stroke in mask viewBox units (matches default glyph weight). */
export const SKILL_BADGE_LUCIDE_MASK_STROKE_WIDTH = 2;

/** Thicker mask stroke for the dark vector outline behind the gold glyph. */
export const SKILL_BADGE_LUCIDE_OUTLINE_MASK_STROKE_WIDTH = 3.35;

type LucideIconNodeElement = [string, Record<string, string>];

function lucideIconNodeElementToSvg([
	tag,
	attrs,
]: LucideIconNodeElement): string {
	const attrPairs = Object.entries(attrs)
		.filter(([key]) => key !== "key")
		.map(
			([key, value]) =>
				`${key}="${String(value).replace(/"/g, "&quot;")}"`,
		);
	return `<${tag} ${attrPairs.join(" ")}/>`;
}

export function buildLucideIconMaskImage(
	iconNode: LucideIconNodeElement[],
	strokeWidth = SKILL_BADGE_LUCIDE_MASK_STROKE_WIDTH,
): string {
	const inner = iconNode.map(lucideIconNodeElementToSvg).join("");
	const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
	return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

const maskImageByIconName = new Map<string, string>();

export function resolveSkillBadgeLucideMaskImage(
	icon: string | null | undefined,
): string {
	const normalized = normalizeSkillLucideIcon(
		icon ?? DEFAULT_SKILL_LUCIDE_ICON,
	);
	const cached = maskImageByIconName.get(normalized);
	if (cached) {
		return cached;
	}

	const iconNode =
		SKILL_LUCIDE_ICON_NODE_BY_NAME[normalized] ??
		SKILL_LUCIDE_ICON_NODE_BY_NAME[DEFAULT_SKILL_LUCIDE_ICON];
	const maskImage = buildLucideIconMaskImage(iconNode);
	maskImageByIconName.set(normalized, maskImage);
	return maskImage;
}

const outlineMaskImageByIconName = new Map<string, string>();

export function resolveSkillBadgeLucideOutlineMaskImage(
	icon: string | null | undefined,
): string {
	const normalized = normalizeSkillLucideIcon(
		icon ?? DEFAULT_SKILL_LUCIDE_ICON,
	);
	const cached = outlineMaskImageByIconName.get(normalized);
	if (cached) {
		return cached;
	}

	const iconNode =
		SKILL_LUCIDE_ICON_NODE_BY_NAME[normalized] ??
		SKILL_LUCIDE_ICON_NODE_BY_NAME[DEFAULT_SKILL_LUCIDE_ICON];
	const maskImage = buildLucideIconMaskImage(
		iconNode,
		SKILL_BADGE_LUCIDE_OUTLINE_MASK_STROKE_WIDTH,
	);
	outlineMaskImageByIconName.set(normalized, maskImage);
	return maskImage;
}
